/**
 * Bits every renderer needs. Kept here so the six renderers stay focused on
 * their own layout maths instead of repeating svg boilerplate.
 */
import type { CSSProperties } from 'react';

import { CANVAS, MONO } from '../theme';

/** Keeps a tall view (deep tree, big grid) from eating the whole viewport. */
export const MAX_CANVAS_HEIGHT = 440;

export const FONT = {
  value: 16,
  caption: 13,
  index: 12,
  label: 11,
} as const;

export const LAYOUT = {
  captionBand: 24,
  indexBand: 20,
  pointerRow: 20,
  labelBand: 14,
} as const;

const round = (n: number) => Math.round(n * 100) / 100;

/** Horizontal breathing room reserved on each side of a caption. */
const PAD_X = 12;

/**
 * Advance width of one caption character. The caption is drawn in the mono
 * stack, so a per-character estimate is accurate enough and keeps layout a pure
 * function of props — measuring the DOM would break snapshot determinism.
 */
const CAPTION_CHAR_W = FONT.caption * 0.62;

export function captionWidth(text?: string): number {
  return text ? text.length * CAPTION_CHAR_W : 0;
}

/** Same estimate for the smaller mark-label font. */
export function labelWidth(text?: string): number {
  return text ? text.length * FONT.label * 0.62 : 0;
}

/** Same estimate for the node-value font used inside graph/tree circles. */
export function valueWidth(text?: string): number {
  return text ? text.length * FONT.value * 0.62 : 0;
}

/**
 * Minimum circle radius that keeps a node's own text from overflowing it.
 * DSA content's node text is short enough that this always resolves to
 * `baseRadius`; longer content (e.g. a model name) grows the circle instead
 * of letting the text run past its edge.
 */
export function nodeRadiusFor(text: string, baseRadius: number): number {
  return Math.max(baseRadius, valueWidth(text) / 2 + 10);
}

/**
 * Shared `<svg>` attributes. The viewBox carries the intrinsic layout size and
 * the element itself is width-fluid, which is what makes every view legible on
 * a 390px phone without any measurement.
 *
 * `caption` widens the viewBox when the caption is longer than the content it
 * labels — a bucket table three columns wide with a sentence-long caption would
 * otherwise have that caption clipped at the SVG edge.
 */
export function frameProps(width: number, height: number, caption?: string) {
  const w = Math.max(1, round(Math.max(width, captionWidth(caption) + PAD_X * 2)));
  const h = Math.max(1, round(height));
  return {
    viewBox: `0 0 ${w} ${h}`,
    preserveAspectRatio: 'xMidYMid meet' as const,
    className: 'h-auto w-full',
    style: { maxHeight: `${Math.min(Math.round(h), MAX_CANVAS_HEIGHT)}px` } as CSSProperties,
    role: 'img' as const,
    fontFamily: MONO,
  };
}

export function fmt(value: number | string | undefined | null): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

export const isNum = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/** A view with no data still needs an accessible name, so it renders as a stub. */
export function EmptyCanvas({ title }: { title: string }) {
  return (
    <svg {...frameProps(160, 56)}>
      <title>{title}</title>
    </svg>
  );
}

export function Caption({ text, x, y }: { text: string; x: number; y: number }) {
  return (
    <text
      x={x}
      y={y}
      aria-hidden="true"
      fill={CANVAS.textDim}
      fontSize={FONT.caption}
      dominantBaseline="central"
    >
      {text}
    </text>
  );
}

/** Small triangle used by pointer labels. `up` points at the thing above it. */
export function PointerTip({
  x,
  y,
  color,
  up = true,
}: {
  x: number;
  y: number;
  color: string;
  up?: boolean;
}) {
  const d = up ? `M ${x} ${y} L ${x - 5} ${y + 6} L ${x + 5} ${y + 6} Z` : `M ${x - 5} ${y} L ${x + 5} ${y} L ${x} ${y + 6} Z`;
  return <path d={d} fill={color} aria-hidden="true" />;
}

export const MONO_FONT = MONO;
